/* eslint-disable react/prop-types */
import { cx } from 'emotion';
import React, {
  createElement,
  memo,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';

import { shallowEqual, DeviceInfo } from '@ezugi/utils';

import * as styles from './style';

// IMPORTANT: not 100% AbortSignal nor AbortController API
function AbortContollerP() {
  return {
    signal: {
      aborted: false,
    },
  };
}

AbortContollerP.prototype.abort = function abort() {
  this.signal.aborted = true;
};

const getComputedFontSize = (element, ratio) => `${parseFloat(getComputedStyle(element).fontSize) * ratio}px`;
const getElementWidth = (element) => {
  let width = element.offsetWidth;
  if (typeof getComputedStyle === 'function') {
    const computedStyles = getComputedStyle(element);
    const hPadding = parseFloat(computedStyles.paddingLeft) + parseFloat(computedStyles.paddingRight);

    width -= hPadding;
  }

  return width;
};

const SafeAbortController = typeof AbortController === 'function' ? AbortController : AbortContollerP;

const AdaptiveTextChild = forwardRef(({ as = 'div', tag: _tag, ...props }, ref) => {
  const containerRef = useRef();
  const [ fontSize, setFontSize ] = useState();

  useImperativeHandle(ref, () => containerRef.current);

  const updateFontSize = (width) => {
    const ratio = width / containerRef.current.scrollWidth;

    if (ratio < 1) {
      const roundedRatio = parseFloat((Math.ceil(ratio * 1000) / 1000).toFixed(2));
      const nextFontSize = getComputedFontSize(containerRef.current, roundedRatio);

      containerRef.current?.childNodes?.forEach?.((element) => {
        if (element.style?.fontSize) {
          element.style.fontSize = getComputedFontSize(element, roundedRatio);
        }
      });
      setFontSize(nextFontSize);
    }
  };

  useLayoutEffect(() => {
    const abortController = new SafeAbortController();
    let intervalHandle;

    if (containerRef.current) {
      let width = getElementWidth(containerRef.current);
      if (!width) {
        intervalHandle = setInterval(() => {
          if (abortController.signal.aborted) clearInterval(intervalHandle);

          width = getElementWidth(containerRef.current);
          const { scrollWidth } = containerRef.current;
          if (width > 0 && width === scrollWidth) clearInterval(intervalHandle);

          !abortController.signal.aborted && updateFontSize(width);
        }, 300);
      } else {
        updateFontSize(width);
      }
    }

    return () => {
      clearInterval(intervalHandle);
      abortController.abort();
    };
  }, []);

  return createElement(as, {
    ...props,
    className: cx(styles.adaptiveText, props.className),
    style: {
      ...props.style,
      fontSize,
    },
    ref: containerRef,
  });
});

const AdaptiveText = (props) => {
  const childRef = useRef();
  const __html = props.dangerouslySetInnerHTML?.__html;
  const __children = props.children;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const key = useMemo(() => childRef.current?.innerHTML, [ __html, __children ]);

  return (
    <AdaptiveTextChild
      {...props}
      key={key}
      ref={childRef}
    />
  );
};

const areTextPropsEqual = (
  { dangerouslySetInnerHTML: pHTML, style: pStyle, ...prevProps },
  { dangerouslySetInnerHTML: nHTML, style: nStyle, ...nextProps }
) => shallowEqual(prevProps, nextProps) && pHTML?.__html === nHTML?.__html && shallowEqual(pStyle, nStyle);

export const DesktopAdaptiveText = ({ text }) => (!DeviceInfo.isHandheld ? (
  <AdaptiveText className={styles.desktopAdaptiveText}>
    {text}
  </AdaptiveText>
) : null);

export default memo(AdaptiveText, areTextPropsEqual);
